"use client";

import { useReducedMotion } from "motion/react";
import { useRef } from "react";
import Comparison from "@/components/preview/comparison";
import DemoHint from "@/components/preview/demo-hint";
import { cn } from "@/lib/utils";

const INTRO_BARS = [
  { id: "line-1", width: "w-full" },
  { id: "line-2", width: "w-11/12" },
  { id: "line-3", width: "w-4/5" },
  { id: "line-4", width: "w-full" },
  { id: "line-5", width: "w-3/5" },
  { id: "line-6", width: "w-full" },
  { id: "line-7", width: "w-5/6" },
  { id: "line-8", width: "w-11/12" },
];

const SECTION_BARS = [
  { id: "line-9", width: "w-full" },
  { id: "line-10", width: "w-4/5" },
  { id: "line-11", width: "w-full" },
  { id: "line-12", width: "w-11/12" },
  { id: "line-13", width: "w-2/3" },
  { id: "line-14", width: "w-full" },
  { id: "line-15", width: "w-5/6" },
  { id: "line-16", width: "w-full" },
  { id: "line-17", width: "w-3/5" },
  { id: "line-18", width: "w-full" },
  { id: "line-19", width: "w-11/12" },
  { id: "line-20", width: "w-1/2" },
];

function MockPage({ scrollMargin }: { scrollMargin: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const reducedMotion = useReducedMotion();

  const jump = () => {
    const container = scrollRef.current;
    const heading = headingRef.current;
    if (!(container && heading)) {
      return;
    }
    const margin =
      Number.parseFloat(getComputedStyle(heading).scrollMarginTop) || 0;
    container.scrollTo({
      behavior: reducedMotion ? "auto" : "smooth",
      top: heading.offsetTop - margin,
    });
  };

  return (
    <div
      className="relative h-56 w-full overflow-y-auto overscroll-contain rounded-lg bg-preview-bg shadow-custom"
      ref={scrollRef}
    >
      <nav className="sticky top-0 z-10 flex h-10 items-center justify-between border-preview-border border-b bg-gray-100 px-3">
        <span aria-hidden="true" className="size-3 rounded-full bg-gray-1200" />
        <button
          className="rounded-sm text-gray-1100 text-sm outline-none transition-colors duration-150 ease-out hover-hover:hover:text-gray-1200 focus-visible:outline-2 focus-visible:outline-sky-400"
          onClick={jump}
          type="button"
        >
          Pricing
        </button>
      </nav>

      <div aria-hidden="true" className="flex flex-col gap-2 px-3 pt-3">
        {INTRO_BARS.map(({ id, width }) => (
          <div className={cn("h-2.5 rounded-sm bg-gray-300", width)} key={id} />
        ))}
      </div>

      <h3
        className={cn(
          "px-3 pt-4 font-semibold text-base text-gray-1200",
          scrollMargin && "scroll-mt-14"
        )}
        ref={headingRef}
      >
        Pricing
      </h3>

      <div aria-hidden="true" className="flex flex-col gap-2 p-3">
        {SECTION_BARS.map(({ id, width }) => (
          <div className={cn("h-2.5 rounded-sm bg-gray-300", width)} key={id} />
        ))}
      </div>
    </div>
  );
}

export default function LayoutScrollMarginDemo() {
  return (
    <Comparison
      bad={<MockPage scrollMargin={false} />}
      good={<MockPage scrollMargin />}
      panelClassName="p-4"
      stackBelow="sm"
      topBar={<DemoHint>Click Pricing in each header</DemoHint>}
    />
  );
}
